import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomButton from "../components/Button";
import Loader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";
import DashTable, {
  BackLink,
  Card,
  KV,
  Oid,
  PageHead,
  RowCard,
  Tag,
} from "../components/DashTable";
import { UI, btnSx } from "../components/ActionDrawer";

const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const LINK_STATUS_META = {
  not_requested: { label: "NOT REQUESTED", tone: "i" },
  link_created: { label: "UNPAID", tone: "s" },
  paid: { label: "PAID", tone: "g" },
};

const TYPE_LABEL = {
  advance: "Advance",
  balance: "Balance on dispatch",
};

const StatusChip = ({ status }) => (
  <Tag tone={LINK_STATUS_META[status]?.tone || "i"}>
    {LINK_STATUS_META[status]?.label || status}
  </Tag>
);

const Btn = ({ variant = "solid", sx, ...props }) => (
  <CustomButton {...props} sx={{ ...btnSx(variant, props.disabled), ...sx }} />
);

const dialogPaperSx = {
  bgcolor: UI.surface,
  backgroundImage: "none",
  border: "1px solid",
  borderColor: UI.line,
  borderRadius: "10px",
};

const dialogTitleSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "10px",
  padding: "16px 18px",
  borderBottom: "1px solid",
  borderColor: UI.line,
};

const DialogHeading = ({ title, sub }) => (
  <Box>
    <Box sx={{ fontSize: 16, fontWeight: 500, color: UI.ink }}>{title}</Box>
    {sub ? (
      <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "2px" }}>{sub}</Box>
    ) : null}
  </Box>
);

const getDefaultDueDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  return date.toISOString().split("T")[0];
};

const ClinicPaymentLinks = () => {
  const { clinicId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [form, setForm] = useState({ orderId: "", amount: "", dueDate: "" });

  const clinicName = location.state?.clinicName || "Clinic";

  const fetchOrders = async () => {
    try {
      const res = await apiService.fetchClinicPaymentLinks(clinicId, token);
      setOrders(res.data || []);
    } catch (error) {
      toast.error("Failed to load payment links for this clinic");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  if (loading) {
    return <Loader />;
  }

  const eligibleOrders = orders.filter(
    (o) => o.paymentType === "advance" && o.outstandingBalance > 0
  );

  const openModal = (orderId) => {
    setForm({
      orderId: orderId || eligibleOrders[0]?.orderId || "",
      amount: "",
      dueDate: getDefaultDueDate(),
    });
    setModalOpen(true);
  };

  const handleCreateLink = async () => {
    const amountValue = Number(form.amount);

    if (!form.orderId) {
      toast.error("Choose an order for this link");
      return;
    }
    if (!amountValue || amountValue <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!form.dueDate) {
      toast.error("Due date is required");
      return;
    }

    setSubmitting(true);
    try {
      await apiService.createAdvancePaymentLink(
        form.orderId,
        { amount: amountValue, dueDate: form.dueDate },
        token
      );
      toast.success("Payment link created");
      setModalOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create payment link"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (orderId) => {
    setResendingId(orderId);
    try {
      await apiService.resendAdvanceLink(orderId, token);
      toast.success("Payment link resent to customer");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to resend payment link"
      );
    } finally {
      setResendingId(null);
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const totalQuoted = orders.reduce((sum, o) => sum + (o.quoteTotal || 0), 0);
  const totalPaid = orders.reduce((sum, o) => sum + (o.amountPaid || 0), 0);
  const totalOutstanding = orders.reduce(
    (sum, o) => sum + (o.outstandingBalance || 0),
    0
  );

  const kpis = [
    { label: "Orders", value: orders.length },
    { label: "Quoted", value: rupee(totalQuoted) },
    { label: "Paid", value: rupee(totalPaid) },
    { label: "Outstanding", value: rupee(totalOutstanding), alarm: totalOutstanding > 0 },
  ];

  const allLinks = orders.flatMap((order) =>
    order.paymentLinks.map((entry) => ({ ...entry, order }))
  );

  const orderColumns = [
    { label: "Order" },
    { label: "Status" },
    { label: "Quoted", num: true },
    { label: "Outstanding", num: true },
    { label: "" },
  ];

  const orderRows = orders.map((order) => {
    const advanceEntry = order.paymentLinks.find((e) => e.type === "advance");
    const canCreate =
      order.paymentType === "advance" && order.outstandingBalance > 0;
    const canResend =
      advanceEntry && advanceEntry.status === "link_created";

    return {
      id: order.orderId,
      key: order.orderId,
      cells: [
        <Box>
          <Box sx={{ fontWeight: 500, color: UI.ink }}>{order.doctorName}</Box>
          <Box sx={{ fontSize: 11, color: UI.ink3 }}>
            {order.paymentType === "advance" ? "Advance" : order.paymentType || "—"}
          </Box>
        </Box>,
        advanceEntry ? <StatusChip status={advanceEntry.status} /> : <Tag tone="i">NO LINK</Tag>,
        rupee(order.quoteTotal),
        rupee(order.outstandingBalance),
        <Box sx={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
          {canCreate && (
            <Btn
              text={advanceEntry ? "New link" : "Create link"}
              variant="ghost"
              width="110px"
              sx={{ textTransform: "none", fontSize: 12 }}
              onClick={(e) => {
                e.stopPropagation();
                openModal(order.orderId);
              }}
            />
          )}
          {canResend && (
            <Btn
              text="Resend"
              variant="ghost"
              width="90px"
              sx={{ textTransform: "none", fontSize: 12 }}
              disabled={resendingId === order.orderId}
              onClick={(e) => {
                e.stopPropagation();
                handleResend(order.orderId);
              }}
            />
          )}
        </Box>,
      ],
    };
  });

  const linkColumns = [
    { label: "Reference" },
    { label: "Order" },
    { label: "Type" },
    { label: "Amount", num: true },
    { label: "Status" },
    { label: "Due date" },
  ];

  const linkRows = allLinks.map((entry) => ({
    id: entry.order.orderId,
    key: entry.id,
    cells: [
      <Oid>{entry.reference}</Oid>,
      entry.order.doctorName,
      TYPE_LABEL[entry.type] || entry.type,
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end" }}>
        <Box component="span" sx={{ fontWeight: 500 }}>{rupee(entry.amount)}</Box>
        {entry.status === "link_created" && entry.razorpayLinkUrl && (
          <Btn
            text="Copy"
            variant="ghost"
            width="60px"
            sx={{ textTransform: "none", fontSize: 11, padding: "2px 8px" }}
            onClick={() => handleCopyLink(entry.razorpayLinkUrl)}
          />
        )}
      </Box>,
      <StatusChip status={entry.status} />,
      entry.dueDate ? new Date(entry.dueDate).toLocaleDateString() : "—",
    ],
  }));

  return (
    <Box
      sx={{
        padding: { xs: "18px 16px 50px", md: "24px 28px 60px" },
        maxWidth: 1240,
      }}
    >
      <BackLink onClick={() => navigate("/dashboard/payment-links")}>
        ‹ Back to payment links
      </BackLink>

      <PageHead title={clinicName} lede="Orders, payment links, and running balance for this clinic." kpis={kpis} />

      <Card title="Orders">
        {orders.length === 0 ? (
          <Box sx={{ fontSize: 13, color: UI.ink3, textAlign: "center", padding: "20px 0" }}>
            No orders for this clinic yet.
          </Box>
        ) : (
          <>
            {/* MOBILE */}
            <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: "10px" }}>
              {orders.map((order) => {
                const advanceEntry = order.paymentLinks.find((e) => e.type === "advance");
                const canCreate = order.paymentType === "advance" && order.outstandingBalance > 0;
                return (
                  <RowCard key={order.orderId}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                      <Box sx={{ fontWeight: 500, color: UI.ink }}>{order.doctorName}</Box>
                      {advanceEntry ? <StatusChip status={advanceEntry.status} /> : <Tag tone="i">NO LINK</Tag>}
                    </Box>
                    <KV label="Quoted" value={rupee(order.quoteTotal)} />
                    <KV label="Outstanding" value={rupee(order.outstandingBalance)} />
                    {canCreate && (
                      <Btn
                        text="Create link"
                        variant="ghost"
                        width="100%"
                        sx={{ textTransform: "none", marginTop: "8px" }}
                        onClick={() => openModal(order.orderId)}
                      />
                    )}
                  </RowCard>
                );
              })}
            </Box>
            {/* DESKTOP */}
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <DashTable columns={orderColumns} rows={orderRows} emptyText="No orders." foot={null} />
            </Box>
          </>
        )}
      </Card>

      <Card title="All payment links">
        {allLinks.length === 0 ? (
          <Box sx={{ fontSize: 13, color: UI.ink3, textAlign: "center", padding: "20px 0" }}>
            No payment links created yet.
          </Box>
        ) : (
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <DashTable columns={linkColumns} rows={linkRows} emptyText="No payment links." foot={null} />
          </Box>
        )}
      </Card>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={dialogTitleSx}>
          <DialogHeading title="Create payment link" sub={clinicName} />
          <IconButton onClick={() => setModalOpen(false)} size="small">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "18px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {eligibleOrders.length > 1 ? (
              <TextField
                select
                label="Order"
                value={form.orderId}
                onChange={(e) => setForm((prev) => ({ ...prev, orderId: e.target.value }))}
                fullWidth
              >
                {eligibleOrders.map((o) => (
                  <MenuItem key={o.orderId} value={o.orderId}>
                    {o.doctorName} — outstanding {rupee(o.outstandingBalance)}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box>
                <Box sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px", color: UI.ink3, fontWeight: 500 }}>
                  Order
                </Box>
                <Box sx={{ fontSize: 14, color: UI.ink, marginTop: "3px" }}>
                  {eligibleOrders[0]
                    ? `${eligibleOrders[0].doctorName} — outstanding ${rupee(eligibleOrders[0].outstandingBalance)}`
                    : "No eligible orders (advance path, balance due)"}
                </Box>
              </Box>
            )}
            <TextField
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Due date"
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm((prev) => ({ ...prev, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end", gap: 1, mt: 3 }}>
            <Btn
              text="Cancel"
              variant="ghost"
              onClick={() => setModalOpen(false)}
              width="120px"
              sx={{ textTransform: "none", width: { xs: "100%", sm: "120px" } }}
            />
            <Btn
              text="Create Razorpay link"
              onClick={handleCreateLink}
              width="200px"
              sx={{ textTransform: "none", width: { xs: "100%", sm: "200px" } }}
              disabled={submitting}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ClinicPaymentLinks;
